// React
import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Refine
import { useTranslation } from '@refinedev/core';

// Mantine
import {
  Accordion,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Modal,
  Stack,
  Text,
  Group,
  Tooltip,
  ActionIcon,
  useComputedColorScheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

// Icons
import { IconInfoCircle } from '@tabler/icons-react';

// Interfaces
import { Claim } from 'interfaces';

// Utils
import { useUpdateIntakeItem } from 'api/resource';
import { getOutlineControlProps } from 'utils/buttonUtils';
import {
  CLAIMS_WINDOW_CHANNEL,
  CLAIMS_WINDOW_TARGET,
  UPLOADS_WINDOW_TARGET,
  QuickCreateType,
  buildQuickCreateHref,
} from 'utils/crossTabNavigation';

// Components
import {
  IntakeNotesPanel,
  LinkedClaimsPanel,
  UploadStatusMenu,
} from './ModalComponents';
import { UploadAttachmentViewer } from './UploadAttachmentViewer';
import { useUploadAttachmentViewer } from './useUploadAttachmentViewer';
import {
  getUploadStatusOptions,
  isUploadStatus,
} from 'pages/uploads/components/uploadStatus';
import { useCanAccess, useCanModify } from 'hooks';

// Sentry
import * as Sentry from '@sentry/react';
import type { IntakeItem, IntakeItemDocument } from '@billing/validation';

const ATTACHMENT_CARD_HEIGHT = '75dvh';
const QUICK_CREATE_ACK_TIMEOUT_MS = 300;

interface UploadViewerModalProps {
  intakeItem: IntakeItem | null;
  modalOpen: boolean;
  setModalOpen: (newValue: boolean) => void;
}

const dedupeClaimsByID = (claims: Claim[]) => {
  const claimMap = new Map<string, Claim>();
  for (const claim of claims) {
    claimMap.set(claim.id, claim);
  }
  return [...claimMap.values()];
};

const formatConfidenceLabel = (confidence: number | null | undefined) => {
  if (typeof confidence !== 'number' || Number.isNaN(confidence)) {
    return 'N/A';
  }

  return `${confidence.toFixed(2)}%`;
};

const getFileNameFromKey = (key: string) => {
  const decoded = decodeURIComponent(key);
  const parts = decoded.split('/');
  return parts[parts.length - 1] || '';
};

const getDocumentAverageConfidence = (document: IntakeItemDocument) => {
  const summary = document.confidenceSummary;

  if (
    typeof summary?.documentConfidence === 'number' &&
    Number.isFinite(summary.documentConfidence)
  ) {
    return summary.documentConfidence;
  }

  const confidences =
    summary?.claims
      ?.map((claim) => claim.confidence)
      .filter(
        (confidence): confidence is number =>
          typeof confidence === 'number' && Number.isFinite(confidence)
      ) ?? [];

  if (!confidences.length) {
    return null;
  }

  const average =
    confidences.reduce((sum, confidence) => sum + confidence, 0) /
    confidences.length;

  return Number(average.toFixed(2));
};

export const UploadViewerModal: React.FC<UploadViewerModalProps> = ({
  intakeItem,
  modalOpen,
  setModalOpen,
}) => {
  const isCompact = useMediaQuery('(max-width: 1200px)');
  const { translate } = useTranslation();
  const { canModify: canModifyUploads } = useCanModify('uploads');
  const { canModify: canModifyClaims } = useCanModify('claims');
  const { canAccess: canAccessClaims } = useCanAccess('claims');

  const computedColorScheme = useComputedColorScheme('dark');

  const updateIntakeItem = useUpdateIntakeItem();

  // Track last persisted status to support rollback if update fails.
  const lastSavedStatusRef = useRef<string | null>(null);

  const [intakeStatus, setIntakeStatus] = useState<string | null>(null);
  const [intakeNotes, setIntakeNotes] = useState('');

  const prefix = useMemo(() => {
    if (!intakeItem?.id || !intakeItem.siteID || !intakeItem.providerID)
      return '';
    return `site/${intakeItem.siteID}/provider/${intakeItem.providerID}/${intakeItem.id}`;
  }, [intakeItem]);

  const attachmentViewer = useUploadAttachmentViewer({ modalOpen, prefix });

  const documentsByFileName = useMemo(
    () =>
      new Map(
        (intakeItem?.documents ?? []).map((document) => [
          document.fileName,
          document,
        ])
      ),
    [intakeItem?.documents]
  );

  const activeAttachmentKey =
    attachmentViewer.fileKeys[attachmentViewer.fileIndex] || '';
  const activeAttachmentName = activeAttachmentKey
    ? getFileNameFromKey(activeAttachmentKey)
    : '';
  const activeDocument = activeAttachmentName
    ? documentsByFileName.get(activeAttachmentName)
    : undefined;
  const activeDocumentConfidenceLabel = activeDocument
    ? `${translate(
        'pages:uploads.uploadsModal.averageDocumentConfidence',
        'Average confidence'
      )}: ${formatConfidenceLabel(
        getDocumentAverageConfidence(activeDocument)
      )}`
    : null;

  useEffect(() => {
    if (window.name === CLAIMS_WINDOW_TARGET) {
      window.name = UPLOADS_WINDOW_TARGET;
    }
  }, []);

  useEffect(() => {
    const nextStatus = intakeItem?.status ?? null;
    const nextNotes = intakeItem?.notes ?? '';
    setIntakeStatus(nextStatus);
    setIntakeNotes(nextNotes);
    lastSavedStatusRef.current = nextStatus;
  }, [intakeItem]);

  const linkedClaims = useMemo(
    () => dedupeClaimsByID((intakeItem?.claims as unknown as Claim[]) || []),
    [intakeItem?.claims]
  );

  const baseStatusOptions = useMemo(
    () => getUploadStatusOptions(translate),
    [translate]
  );

  const statusPlaceholder = translate(
    'pages:uploads.table.selectStatus',
    'Select status'
  );

  const statusOptions = useMemo(() => {
    if (!intakeStatus) return baseStatusOptions;
    if (isUploadStatus(intakeStatus)) return baseStatusOptions;
    return [...baseStatusOptions, { value: intakeStatus, label: intakeStatus }];
  }, [baseStatusOptions, intakeStatus]);

  const handleStatusChange = useCallback(
    async (nextStatus: string) => {
      if (!intakeItem) return;
      if (!canModifyUploads) return;
      if (nextStatus === intakeStatus) return;

      const fallbackStatus = lastSavedStatusRef.current ?? intakeStatus;
      setIntakeStatus(nextStatus);

      try {
        await updateIntakeItem.mutateAsync({
          id: intakeItem.id,
          status: nextStatus,
          notes: intakeNotes || null,
        });
        lastSavedStatusRef.current = nextStatus;
      } catch (error) {
        setIntakeStatus(fallbackStatus ?? null);
        Sentry.captureException(error, {
          tags: { component: 'UploadViewerModal' },
          extra: { intakeItemID: intakeItem.id },
        });
      }
    },
    [canModifyUploads, intakeItem, intakeNotes, intakeStatus, updateIntakeItem]
  );

  const isSavingIntake = updateIntakeItem.isPending;
  const pdfHeight = isCompact ? '62dvh' : '66dvh';
  const documentPreviewTitle = translate(
    attachmentViewer.fileKeys.length > 1
      ? 'pages:uploads.uploadsModal.documentsPreview'
      : 'pages:uploads.uploadsModal.documentPreview',
    attachmentViewer.fileKeys.length > 1
      ? 'Documents Preview'
      : 'Document Preview'
  );

  const quickCreateLinks = useMemo(
    () => [
      {
        key: 'claim' as const,
        label: translate(
          'pages:uploads.uploadsModal.quickCreate.singleClaim',
          'Create Single Claim'
        ),
      },
      {
        key: 'bulk' as const,
        label: translate(
          'pages:uploads.uploadsModal.quickCreate.bulkClaims',
          'Create Batch Claims'
        ),
      },
      {
        key: 'repeat' as const,
        label: translate(
          'pages:uploads.uploadsModal.quickCreate.repeatClaims',
          'Create Repeat Claims'
        ),
      },
    ],
    [translate]
  );

  const openQuickCreateInClaimsWindow = useCallback((href: string) => {
    if (typeof BroadcastChannel === 'undefined') {
      window.open(href, CLAIMS_WINDOW_TARGET);
      return;
    }

    const channel = new BroadcastChannel(CLAIMS_WINDOW_CHANNEL);
    const requestID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    let acknowledged = false;
    let timeoutID = 0;

    const cleanup = () => {
      window.clearTimeout(timeoutID);
      channel.removeEventListener('message', onMessage);
      channel.close();
    };

    const onMessage = (event: MessageEvent) => {
      const message = event.data as
        | { type?: string; requestID?: string }
        | undefined;
      if (
        message?.type === 'claims:quick-create-ack' &&
        message.requestID === requestID
      ) {
        acknowledged = true;
        cleanup();
      }
    };

    channel.addEventListener('message', onMessage);
    timeoutID = window.setTimeout(() => {
      cleanup();
      if (!acknowledged) {
        window.open(href, CLAIMS_WINDOW_TARGET);
      }
    }, QUICK_CREATE_ACK_TIMEOUT_MS);

    const search = new URL(href, window.location.origin).search;
    channel.postMessage({
      type: 'claims:quick-create-request',
      requestID,
      search,
    });
  }, []);

  const handleQuickCreateClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, createType: QuickCreateType) => {
      event.preventDefault();
      const href = buildQuickCreateHref(intakeItem, createType);
      openQuickCreateInClaimsWindow(href);
    },
    [intakeItem, openQuickCreateInClaimsWindow]
  );

  const modalHeader = (
    <Stack gap="xs" w="100%" pr={isCompact ? 0 : 'sm'}>
      <Text fw={700} size="xl">
        {translate('pages:uploads.uploadsModal.title', {
          intakeNumber: intakeItem?.intakeNumber ?? '',
        })}
      </Text>
      <Flex justify="space-between" align="center" gap="sm" wrap="wrap">
        <UploadStatusMenu
          status={intakeStatus}
          statusOptions={statusOptions}
          onChange={handleStatusChange}
          disabled={!intakeItem || isSavingIntake || !canModifyUploads}
          placeholder={statusPlaceholder}
        />
        <Badge
          data-testid="task-modal-linked-claims-count"
          variant="light"
          color="blue"
          size="md"
          radius="sm"
        >
          {translate(
            'pages:uploads.uploadsModal.linkedClaimsBadge',
            'Linked Claims'
          )}
          : {linkedClaims.length}
        </Badge>
      </Flex>
    </Stack>
  );

  return (
    <Modal
      opened={modalOpen}
      onClose={() => setModalOpen(false)}
      title={modalHeader}
      size={isCompact ? '100%' : 'xl'}
      yOffset="2dvh"
      centered
      withCloseButton
      closeOnEscape
      closeOnClickOutside={false}
      trapFocus
      overlayProps={{ backgroundOpacity: 0.45, blur: 4 }}
      styles={{
        header: { alignItems: 'flex-start' },
        title: { width: '100%' },
      }}
    >
      <Flex direction="column" gap="md">
        <Box
          style={{
            flex: isCompact ? undefined : 1,
            minHeight: 0,
            overflow: 'auto',
            paddingRight: isCompact ? 0 : 4,
          }}
        >
          {intakeItem ? (
            <Stack gap="md" pb="xs">
              <Card withBorder radius="md" padding="md">
                <Stack gap="md">
                  <Accordion defaultValue="document-viewer" variant="contained">
                    <Accordion.Item value="document-viewer">
                      <Accordion.Control>
                        <Text fw={600}>{documentPreviewTitle}</Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Card
                          withBorder
                          radius="md"
                          padding="md"
                          style={{
                            height: ATTACHMENT_CARD_HEIGHT,
                            maxHeight: ATTACHMENT_CARD_HEIGHT,
                            minHeight: isCompact ? '48dvh' : '40dvh',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                          }}
                        >
                          <UploadAttachmentViewer
                            viewer={attachmentViewer}
                            isCompact={isCompact}
                            pdfHeight={pdfHeight}
                            activeAttachmentName={activeAttachmentName}
                            activeAttachmentConfidenceLabel={
                              activeDocumentConfidenceLabel
                            }
                          />
                        </Card>
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>

                  <IntakeNotesPanel notes={intakeNotes} />
                </Stack>
              </Card>

              {canModifyClaims ? (
                <Card withBorder radius="md" padding="md">
                  <Stack gap="xs">
                    <Group gap={4}>
                      <Text fw={600}>
                        {translate(
                          'pages:uploads.uploadsModal.quickCreateLinksTitle',
                          'Quick Create Links'
                        )}
                      </Text>
                      <Tooltip
                        label={translate(
                          'pages:uploads.uploadsModal.quickCreateLinksDescription',
                          'Open claim creation flows in your Claims workspace with task context prefilled.'
                        )}
                      >
                        <ActionIcon variant="subtle" size="sm">
                          <IconInfoCircle size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                    <Flex direction={isCompact ? 'column' : 'row'} gap="sm">
                      {quickCreateLinks.map((createTypeOption) => (
                        <Button
                          key={createTypeOption.key}
                          component="a"
                          href={buildQuickCreateHref(
                            intakeItem,
                            createTypeOption.key
                          )}
                          target={CLAIMS_WINDOW_TARGET}
                          onClick={(event) =>
                            handleQuickCreateClick(event, createTypeOption.key)
                          }
                          data-testid={`quick-create-${createTypeOption.key}`}
                          fullWidth
                          {...getOutlineControlProps(
                            computedColorScheme,
                            false
                          )}
                        >
                          {createTypeOption.label}
                        </Button>
                      ))}
                    </Flex>
                  </Stack>
                </Card>
              ) : null}

              <LinkedClaimsPanel
                claims={linkedClaims}
                intakeItem={intakeItem}
                documents={intakeItem.documents}
                uploadClaimsWindowTarget={CLAIMS_WINDOW_TARGET}
                canAccessClaims={canAccessClaims}
                canOpenLinkedClaims={canModifyClaims}
              />

              <Card withBorder radius="md" padding="md">
                <Stack gap="xs">
                  <Text fw={600}>
                    {translate(
                      'pages:uploads.uploadsModal.internalCommentsTitle',
                      'Internal Comments'
                    )}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {translate(
                      'pages:uploads.uploadsModal.internalCommentsDescription',
                      'Feature Coming Soon! Section for task comments that are visible to internal users but hidden from providers and members.'
                    )}
                  </Text>
                </Stack>
              </Card>
            </Stack>
          ) : (
            <Flex align="center" justify="center" h="100%">
              <Text size="sm" c="dimmed">
                {translate(
                  'pages:uploads.uploadsModal.selectUpload',
                  'Select an upload to start.'
                )}
              </Text>
            </Flex>
          )}
        </Box>
      </Flex>
    </Modal>
  );
};
